/**
 * MailingAddressPrompt — asks for the family's mailing address once, after a
 * purchase (train 17, operator ruling 2026-10-23). Mounted by BillingChrome and
 * by SubscribedPage, where a web subscribe lands.
 *
 * Triggered by `requestMailingPrompt()` (adding a card, a user-started Apple
 * purchase the server accepted) or the flag `noteMailingPrompt()` leaves for the
 * next page, read on mount. It shows only to a grown-up session (never a child: useIsChildSession, not role alone) and only
 * when GET says the family was never asked; a co-parent's GET is a 403 and
 * shows nothing. Closing it counts as Skip, posted once.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../hooks/useAuth";
import { useIsChildSession } from "../hooks/useIsChildSession";
import { isGrownUpRole } from "./grownUpRole";
import { MailingAddressSheet } from "./MailingAddressSheet";
import {
  MAILING_PROMPT_EVENT,
  getMailingAddress,
  saveMailingAddress,
  skipMailingAddress,
  takeMailingPrompt,
  type MailingAddress,
} from "./mailingAddress";

export function MailingAddressPrompt({ outsideAuth = false }: {
  /** SubscribedPage has no auth providers, so no role: only a paying account reaches it, and
   *  the server still answers 403 to anyone but the account holder. */
  outsideAuth?: boolean;
}): JSX.Element | null {
  const role = useContext(AuthContext)?.role;
  const isChild = useIsChildSession();
  const grownUp = outsideAuth || (isGrownUpRole(role) && !isChild);
  const [open, setOpen] = useState(false);
  const answered = useRef(false);

  const consider = useCallback(() => {
    // Role first: on the load after /subscribed the role can arrive a render
    // late, and taking the flag before then would lose the ask for good.
    if (!grownUp || !takeMailingPrompt()) return;
    getMailingAddress()
      .then((s) => {
        if (!s.asked) {
          answered.current = false;
          setOpen(true);
        }
      })
      .catch(() => { /* a co-parent (403), or offline: ask nothing */ });
  }, [grownUp]);

  useEffect(() => {
    consider();
    window.addEventListener(MAILING_PROMPT_EVENT, consider);
    return () => window.removeEventListener(MAILING_PROMPT_EVENT, consider);
  }, [consider]);

  const skip = () => {
    setOpen(false);
    if (answered.current) return;
    answered.current = true;
    void skipMailingAddress().catch(() => { /* asked again after the next purchase */ });
  };

  const save = async (address: MailingAddress) => {
    await saveMailingAddress(address);
    answered.current = true;
    setOpen(false);
  };

  return <MailingAddressSheet open={open} onSave={save} onSkip={skip} />;
}
